import Highchart from '@/components/common/highchart/highchart.vue'
import StatisticAnalysisExternalViewGeneralStatistics from '@/modules/statistical-analysis-module/components/statistical-analysis-external-views/statistical-analysis-external-views-general-statistics/statistical-analysis-external-views-general-statistics.vue'
import { initialStatisticalAnalysisGeneralStatisticsGeneral, StatisticalAnalysisGeneralStatisticsGeneralState } from '@/modules/statistical-analysis-module/components/statistical-analysis-general-statistics-general/statistical-analysis-general-statistics-general.model'
import { StatisticalAnalysisGeneralStatisticsDossierTrackingElements } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.dto'
import { generalStatisticsDossierService } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.service'
import { StatisticalAnalysisHeaderNavigationItems } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-header/statistical-analysis-header.models'
import StatisticalAnalysisRangeSelector from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-range-selector/statistical-analysis-range-selector.vue'
import StatisticalAnalysisStatusLine from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-status-line/statistical-analysis-status-line.vue'
import { StatisticalAnalysisCaseTypes } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisGeneralDossierExternalInfo, StatisticsAnalysisGeneralStatisticsTrackingItems } from '@/modules/statistical-analysis-module/views/general-statistics-dossier/general-statistics-dossier.model'
import { PointOptionsObject, SeriesColumnOptions } from 'highcharts'
import { findIndex } from 'lodash'
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator'
import StatisticalAnalysisSkeletonGeneralDossierTrackingItems from '@/modules/statistical-analysis-module/components/statistical-analysis-skeletons/statistical-analysis-skeleton-general-dossier/statistical-analysis-skeleton-general-dossier-tracking-items/statistical-analysis-skeleton-general-dossier-tracking-items.vue'

@Component({
    components: {
        Highchart,
        StatisticalAnalysisStatusLine,
        StatisticalAnalysisRangeSelector,
        StatisticAnalysisExternalViewGeneralStatistics,
        StatisticalAnalysisSkeletonGeneralDossierTrackingItems
    }
})
export default class StatisticalAnalysisGeneralStatisticsGeneral extends Vue {
    @Prop() readonly externalInfo!: StatisticalAnalysisGeneralDossierExternalInfo
    @Prop() readonly navigationItems!: StatisticalAnalysisHeaderNavigationItems<any>[]
    @Emit()
    statusSelection(state: StatisticsAnalysisGeneralStatisticsTrackingItems): StatisticsAnalysisGeneralStatisticsTrackingItems {
        return state
    }
    @Emit()
    trackingItemsLoaded(trackingItems: StatisticsAnalysisGeneralStatisticsTrackingItems[]): StatisticsAnalysisGeneralStatisticsTrackingItems[] {
        return trackingItems
    }
    @Emit()
    rangeChange(value: boolean): boolean {
        return value
    }
    @Watch('externalInfo', { deep: true, immediate: true })
    onExternalInfoChanged(externalInfo: StatisticalAnalysisGeneralDossierExternalInfo) {
        if (externalInfo) {
            this.$nextTick(() => {
                this.state.promiseGeneralViewTracking = this._getInfoGeneralView()
            })
        }
    }

    state: StatisticalAnalysisGeneralStatisticsGeneralState
    constructor() {
        super()
        this.state = initialStatisticalAnalysisGeneralStatisticsGeneral()
    }

    get sameRangeValue(): boolean {
        return this.externalInfo.sameRange
    }

    onStatusSelection(state: StatisticsAnalysisGeneralStatisticsTrackingItems): void {
        this.trackingItemsLoaded(this.state.trackingItems)
        this.state.itemSelected = state
        this.statusSelection(state)
    }

    onRangeChange(value: boolean): void {
        this.rangeChange(value)
    }

    private _updateSeries(trackingItems: StatisticsAnalysisGeneralStatisticsTrackingItems[]): Array<SeriesColumnOptions> {
        const filteredSeries = trackingItems.filter((trackItem: StatisticsAnalysisGeneralStatisticsTrackingItems) => trackItem.active)
        return [{
            type: 'column',
            colorByPoint: true,
            name: this.$tc(this.externalInfo?.typeSearch?.text as string, 2),
            data: filteredSeries?.map((itemSerie:StatisticsAnalysisGeneralStatisticsTrackingItems) => {
                const point: PointOptionsObject = {
                    name: itemSerie.label,
                    y: itemSerie.percent,
                    drilldown: itemSerie.label
                }
                return point
            })
        }]
    }

    private _updateChart(trackingItems: StatisticsAnalysisGeneralStatisticsTrackingItems[]): void {
        this.state = {
            ...this.state,
            chartOptions: {
                ...this.state.chartOptions,
                series: this._updateSeries(trackingItems),
                yAxis: {
                    title: {
                        text: `${this.$tc('statisticalAnalysis.charts.totalOf')} ${this.$tc(this.externalInfo?.typeSearch?.text as string, 2).toLowerCase()}`
                    }
                }
            }
        }
    }

    private async _getInfoGeneralView(): Promise<void> {
        const response: StatisticalAnalysisGeneralStatisticsDossierTrackingElements[] = await this.$_toCallSecure<StatisticalAnalysisGeneralStatisticsDossierTrackingElements[]>(generalStatisticsDossierService.getGeneralViewTracking(generalStatisticsDossierService.getBaseRequest(this.externalInfo), this.externalInfo.trackingGeneralSelected?.key as StatisticalAnalysisCaseTypes, this.externalInfo.typeSearch!)) as StatisticalAnalysisGeneralStatisticsDossierTrackingElements[]
        this.state.trackingItems = this._transformTrackingItem(response)
        this.state.trackingItems = this._parseTrackingItems(this.navigationItems, this.externalInfo.trackingGeneralSelected!)
        this._updateChart(this.state.trackingItems)
    }

    private _transformTrackingItem(data:StatisticalAnalysisGeneralStatisticsDossierTrackingElements[]): StatisticsAnalysisGeneralStatisticsTrackingItems[] {
        return data.map((item: StatisticalAnalysisGeneralStatisticsDossierTrackingElements) => this._getTrackItem(item) as StatisticsAnalysisGeneralStatisticsTrackingItems)
    }

    private _getTrackItem(item: StatisticalAnalysisGeneralStatisticsDossierTrackingElements): StatisticsAnalysisGeneralStatisticsTrackingItems | StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisCaseTypes> {
        return {
            label: item?.state.name,
            key: item?.state.code,
            value: item?.total,
            active: true,
            percent: 0
        }
    }

    private _parseTrackingItems(data:StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisCaseTypes>[], itemSelected: StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisCaseTypes>): StatisticsAnalysisGeneralStatisticsTrackingItems[] {
        const indexSelected: number = findIndex(data, ['key', itemSelected.key])
        return this.state.trackingItems.map((item: StatisticsAnalysisGeneralStatisticsTrackingItems, index: number) => {
            item.active = index >= indexSelected
            item.percent = this._calculatePercent(item.value, itemSelected)
            return item
        })
    }

    private _calculatePercent(value: number, itemSelected: StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisCaseTypes>): number {
        const totalValue = itemSelected.value
        let result = 0
        if (!totalValue || !value) {
            result = 0
            return parseFloat(result.toFixed(1))
        } else {
            result = value * 100 / totalValue
            return parseFloat(result.toFixed(1))
        }
    }
}
